import React, { useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { CursosService } from '../../../../service/cursos.service';
import { Curso } from '../../../../service/types/cursos.types';
import { FeedbackModal } from '../../feedback.modal';
import SignaturePad from '../../../pages/m4/predioPrograma/signaturePad';

interface CursoFormProps {
  initialValue?: Curso;
  onSaved: (curso: Curso) => void;
  onCancel?: () => void;
}

export default function CursoForm({ initialValue, onSaved, onCancel }: CursoFormProps) {
  const [titulo, setTitulo] = useState(initialValue?.titulo ?? '');
  const [descripcion, setDescripcion] = useState(initialValue?.descripcion ?? '');
  const [fechaLimite, setFechaLimite] = useState(initialValue?.fechaLimite?.slice(0, 10) ?? '');
  const [duracion, setDuracion] = useState<string>(initialValue?.duracion?.toString() ?? '');
  const [vigencia, setVigencia] = useState<string>(initialValue?.vigencia?.toString() ?? '');
  const [firmaAdmin, setFirmaAdmin] = useState(initialValue?.firmaAdmin ?? '');
  const [activo, setActivo] = useState(initialValue?.activo ?? true);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [modalMessage, setModalMessage] = useState('');

  const isEditing = !!initialValue?.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim()) {
      alert('Ingrese un título para el curso');
      return;
    }
    if (!fechaLimite) {
      alert('Ingrese una fecha límite');
      return;
    }
    if (!firmaAdmin) {
      alert('Debe firmar como administrador antes de guardar el curso');
      return;
    }
    setSaving(true);
    try {
      // Se envía `null` explícito (no `undefined`) para poder limpiar un valor ya guardado:
      // axios/JSON omiten las claves `undefined`, lo que dejaría el valor anterior intacto.
      const duracionValue = duracion.trim() ? Number(duracion) : null;
      const vigenciaValue = vigencia.trim() ? Number(vigencia) : null;
      const curso = isEditing
        ? await CursosService.update(initialValue!.id, {
            titulo,
            descripcion,
            fechaLimite,
            duracion: duracionValue,
            vigencia: vigenciaValue,
            firmaAdmin,
            activo,
          })
        : await CursosService.create({
            titulo,
            descripcion,
            fechaLimite,
            duracion: duracionValue,
            vigencia: vigenciaValue,
            firmaAdmin,
            activo,
          });
      setModalType('success');
      setModalMessage(isEditing ? 'Curso actualizado correctamente' : 'Curso creado correctamente');
      setShowModal(true);
      onSaved(curso);
    } catch (error) {
      setModalType('error');
      setModalMessage('Error al guardar el curso');
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-3">
      <FeedbackModal
        show={showModal}
        onHide={() => setShowModal(false)}
        type={modalType}
        message={modalMessage}
        autoClose
        duration={2500}
      />
      <Card.Header>{isEditing ? 'Editar curso' : 'Crear curso'}</Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Título</Form.Label>
            <Form.Control
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ej: Inducción de seguridad"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha límite</Form.Label>
                <Form.Control
                  type="date"
                  value={fechaLimite}
                  onChange={(e) => setFechaLimite(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Duración aproximada (horas)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.5"
                  value={duracion}
                  onChange={(e) => setDuracion(e.target.value)}
                  placeholder="Ej: 4"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Vigencia (meses)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="1"
                  value={vigencia}
                  onChange={(e) => setVigencia(e.target.value)}
                  placeholder="Vacío = duración indefinida"
                />
                <Form.Text muted>
                  Vacío: el curso no vuelve a pedirse una vez completado. Con un valor, ej. 6, el curso
                  vuelve a aparecer como pendiente 6 meses después de completado.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Firma del administrador</Form.Label>
            {initialValue?.firmaAdmin && (
              <div className="mb-2">
                <div className="text-muted small">Firma actual (dibuje una nueva para reemplazarla):</div>
                <img
                  src={initialValue.firmaAdmin}
                  alt="Firma actual del administrador"
                  style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #ccc' }}
                />
              </div>
            )}
            <SignaturePad onChange={setFirmaAdmin} />
            <Form.Text muted>
              Esta firma se imprime en el certificado de completitud que recibe cada alumno.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Activo"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button type="submit" variant="success" disabled={saving}>
              {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear curso'}
            </Button>
            {onCancel && (
              <Button variant="secondary" onClick={onCancel} disabled={saving}>
                Cancelar
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
